import {
  InsuranceType,
  ProductBackendName,
} from "Components/Features/CheckPrice/Products/productTypes";
import {
  CoverageTexts,
  InsuranceCoverage,
} from "Store/interfaces/products/coverage";
import { Field, FieldProps, useField } from "formik";
import { CoverageWrap, Description } from "./coverages.styles";

import { InsuranceTypeVariantVariable } from "Components/Features/CheckPrice/Tiles/ProductPriceTile/Coverages/coverages.helpers";
import { SelectableCoverageCard } from "Components/Form/Card/SelectableCoverageCard";
import { getComponentTextByFieldId } from "Components/TextContext";
import { Data } from "Components/TextContext/textInterfaces";
import { CalculationPrice } from "Store/priceCalculation/priceCalculationInterfaces";
import { isDefined } from "Utils/functions";
import React from "react";
import ReactMarkdown from "react-markdown";
import { CoverageCardDetails } from "./CoverageCardDetails";
import { CoverageCardPrice } from "./CoverageCardPrice";
import { ExtendedCoverage } from "./ExtendedCoverage";
import { useGetCurrentPrices } from "./coverages.hooks";
import { CoveragePriceCalculation } from "./coverages.types";
interface Props {
  readonly fieldName: string;
  readonly product: ProductBackendName;
  readonly coverage: InsuranceCoverage;
  readonly textComponents: Data[];
  readonly prices: CalculationPrice[];
  readonly priceFilterValue?: string;
  readonly shortTermProduct?: boolean;
  readonly hideRadioButton?: boolean;
  readonly hideDescription?: boolean;
  readonly disabled?: boolean;
  readonly disabledCoverageDesc?: React.ReactNode;
  readonly showExcludedCoverages?: boolean;
  readonly priceIsLoading?: boolean;
  readonly extendedCoverage?: CoveragePriceCalculation;
  readonly doNotShow?: InsuranceType;
  readonly overriddenBetterInsuranceText?: string;
  readonly overriddenIncludedCoverageTexts?: CoverageTexts[];
  readonly overriddenExcludedCoverageTexts?: CoverageTexts[];
  readonly insuranceTypeVariant?: InsuranceTypeVariantVariable;
}

export const CoverageCard = ({
  fieldName,
  product,
  coverage,
  textComponents,
  prices,
  priceFilterValue,
  shortTermProduct,
  hideRadioButton,
  hideDescription,
  disabled,
  disabledCoverageDesc,
  showExcludedCoverages = true,
  priceIsLoading,
  extendedCoverage,
  doNotShow,
  overriddenBetterInsuranceText,
  overriddenExcludedCoverageTexts,
  overriddenIncludedCoverageTexts,
  insuranceTypeVariant,
  children,
}: React.PropsWithChildren<Props>): React.ReactElement => {
  const [, fieldMeta] = useField<InsuranceCoverage>(fieldName);
  const { monthlyPrice, yearlyPrice } = useGetCurrentPrices({
    prices,
    priceFilterValue,
  });

  const isCoverageSelected =
    isDefined(fieldMeta.value) && fieldMeta.value === coverage;
  const isExtendedCoverageSelected =
    isDefined(fieldMeta.value) && fieldMeta.value === extendedCoverage?.[0];

  return (
    <Field name={fieldName} value={coverage} type="radio">
      {({ field }: FieldProps) => (
        <SelectableCoverageCard
          id={coverage}
          {...field}
          type="radio"
          value={coverage}
          label={
            getComponentTextByFieldId(
              textComponents,
              `coveragelabel-${coverage.toLowerCase()}`,
            ) ?? ""
          }
          labelDetails={
            !disabled && (
              <CoverageCardPrice
                shortTermProduct={shortTermProduct}
                price={shortTermProduct ? yearlyPrice : monthlyPrice}
                priceIsLoading={priceIsLoading}
              />
            )
          }
          hideRadioButton={hideRadioButton}
          removePaddingLeft={hideRadioButton}
          disabled={disabled}
          checked={field.checked || isExtendedCoverageSelected}
        >
          <CoverageWrap removePaddingLeft={hideRadioButton}>
            {!hideDescription &&
              (disabled && isDefined(disabledCoverageDesc) ? (
                disabledCoverageDesc
              ) : (
                <Description fw={400} size={"14px"}>
                  <ReactMarkdown>
                    {getComponentTextByFieldId(
                      textComponents,
                      `description-${coverage.toLowerCase()}`,
                    ) ?? ""}
                  </ReactMarkdown>
                </Description>
              ))}

            {!disabled && isDefined(extendedCoverage) && (
              <ExtendedCoverage
                baseCost={monthlyPrice}
                baseCoverage={coverage}
                coverage={extendedCoverage}
                fieldName={fieldName}
                priceFilterValue={priceFilterValue}
                textComponents={textComponents}
              />
            )}

            {children}

            <CoverageCardDetails
              product={product}
              cardIsSelected={isCoverageSelected || isExtendedCoverageSelected}
              coverage={
                isDefined(extendedCoverage) &&
                fieldMeta.value === extendedCoverage[0]
                  ? extendedCoverage[0]
                  : coverage
              }
              showExcludedCoverages={showExcludedCoverages}
              textComponents={textComponents}
              doNotShow={doNotShow}
              overriddenBetterInsuranceText={overriddenBetterInsuranceText}
              overriddenExcludedCoverageTexts={overriddenExcludedCoverageTexts}
              overriddenIncludedCoverageTexts={overriddenIncludedCoverageTexts}
              insuranceTypeVariant={insuranceTypeVariant}
            />
          </CoverageWrap>
        </SelectableCoverageCard>
      )}
    </Field>
  );
};
